import { useState } from 'react'
import { useQueryClient } from 'react-query'
import { DateTime } from 'luxon'
import styled from 'styled-components'
import {
    useGetInternalDashboardSummary,
    useGetInternalEmailTests,
    useGetInternalFeatureFlags,
    useGetInternalUsage,
    useSendInternalTestEmail,
    useUpdateEmailDelivery,
    useUpdateInternalFeatureFlags,
} from '../../services/api/internal.hooks'
import { Border, Colors, Dimensions, Spacing, Typography } from '../../styles'
import { icons } from '../../styles/images'
import {
    TEmailDeliveryConfigIssue,
    TInternalDashboardCounts,
    TInternalDashboardUser,
    TInternalUsage,
    TInternalUsageWindow,
} from '../../utils/types'
import GTSwitch from '../atoms/GTSwitch'
import { Icon } from '../atoms/Icon'
import Spinner from '../atoms/Spinner'
import GTButton from '../atoms/buttons/GTButton'
import NoStyleButton from '../atoms/buttons/NoStyleButton'
import {
    BodyMedium,
    BodySmall,
    BodySmallUpper,
    HeadlineMedium,
    LabelSmall,
    TitleSmall,
} from '../atoms/typography/Typography'
import { CalendarContextProvider } from '../calendar/CalendarContext'
import DefaultTemplate from '../templates/DefaultTemplate'

const numberFormatter = new Intl.NumberFormat()

const DashboardPage = styled.div`
    flex: 1;
    min-width: 0;
    overflow-y: auto;
    padding: ${Spacing._32};
    background-color: ${Colors.background.page};

    ${Dimensions.mediaQuery.phone} {
        padding: ${Spacing._16};
    }
`

const DashboardInner = styled.div`
    display: flex;
    flex-direction: column;
    gap: ${Spacing._24};
    max-width: 1040px;
`

const Header = styled.div`
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: ${Spacing._16};

    ${Dimensions.mediaQuery.phone} {
        flex-direction: column;
        align-items: stretch;
    }
`

const HeaderText = styled.div`
    display: flex;
    flex-direction: column;
    gap: ${Spacing._4};
`

const MetricsGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(4, minmax(0, 1fr));
    gap: ${Spacing._12};

    ${Dimensions.mediaQuery.tabletOrSmaller} {
        grid-template-columns: repeat(2, minmax(0, 1fr));
    }

    ${Dimensions.mediaQuery.phone} {
        grid-template-columns: 1fr;
    }
`

const MetricPanel = styled.div<{ $highlight?: boolean }>`
    display: flex;
    flex-direction: column;
    gap: ${Spacing._8};
    min-width: 0;
    padding: ${Spacing._16};
    border: ${Border.stroke.medium} solid
        ${({ $highlight }) => ($highlight ? Colors.accent.yellowStroke : Colors.background.border)};
    border-radius: ${Border.radius.medium};
    background-color: ${Colors.background.white};
`

const MetricValue = styled.span`
    ${Typography.headline.medium};
    color: ${Colors.text.ink};
    font-variant-numeric: tabular-nums;
`

const Panel = styled.div`
    border: ${Border.stroke.medium} solid ${Colors.background.border};
    border-radius: ${Border.radius.medium};
    background-color: ${Colors.background.white};
    overflow: hidden;
`

const PanelHeader = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: ${Spacing._16};
    padding: ${Spacing._16};
    border-bottom: ${Border.stroke.medium} solid ${Colors.background.border};
`

const TableScroller = styled.div`
    overflow-x: auto;
`

const UserTable = styled.table`
    width: 100%;
    min-width: 840px;
    border-collapse: collapse;

    th,
    td {
        padding: ${Spacing._12} ${Spacing._16};
        border-bottom: ${Border.stroke.medium} solid ${Colors.background.hairline};
        text-align: left;
        vertical-align: top;
    }

    th {
        ${Typography.label.small};
        color: ${Colors.text.muted};
        text-transform: uppercase;
    }

    tr:last-child td {
        border-bottom: none;
    }
`

const UserCell = styled.div`
    display: flex;
    flex-direction: column;
    gap: ${Spacing._4};
    min-width: 0;
`

const Email = styled.span`
    ${Typography.body.medium};
    color: ${Colors.text.ink};
    word-break: break-word;
`

const MonoValue = styled.span`
    ${Typography.body.medium};
    color: ${Colors.text.ink};
    font-variant-numeric: tabular-nums;
`

const Tag = styled.span<{ $paid?: boolean }>`
    ${Typography.label.small};
    display: inline-flex;
    width: fit-content;
    padding: ${Spacing._2} ${Spacing._8};
    border: ${Border.stroke.medium} solid
        ${({ $paid }) => ($paid ? Colors.accent.yellowStroke : Colors.background.border)};
    border-radius: ${Border.radius.medium};
    color: ${Colors.text.ink};
    background-color: ${({ $paid }) => ($paid ? Colors.accent.yellow : Colors.background.sub)};
`

const EmptyState = styled.div`
    padding: ${Spacing._32};
`

const FeatureFlagRow = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: ${Spacing._24};
    padding: ${Spacing._16};

    & + & {
        border-top: ${Border.stroke.medium} solid ${Colors.background.border};
    }

    ${Dimensions.mediaQuery.phone} {
        align-items: flex-start;
    }
`

const FeatureFlagDetails = styled.div`
    display: flex;
    flex-direction: column;
    gap: ${Spacing._4};
`

const FeatureFlagControl = styled.div`
    display: flex;
    align-items: center;
    gap: ${Spacing._12};
`

const EmailTestResult = styled(LabelSmall)<{ $status: TEmailTestStatus['state'] }>`
    color: ${({ $status }) =>
        $status === 'sent'
            ? Colors.semantic.success.base
            : $status === 'error'
            ? Colors.semantic.warning.base
            : Colors.text.muted};
    max-width: 320px;
    text-align: right;
    overflow-wrap: anywhere;
`

const DeliveryOffBanner = styled.div`
    display: flex;
    align-items: center;
    gap: ${Spacing._8};
    padding: ${Spacing._12} ${Spacing._16};
    border-bottom: ${Border.stroke.medium} solid ${Colors.background.border};
    background-color: ${Colors.background.sub};
`

const HeaderControls = styled.div`
    display: flex;
    align-items: center;
    gap: ${Spacing._16};
`

const SwitchLabel = styled.label`
    display: flex;
    align-items: center;
    gap: ${Spacing._8};
`

const PanelControls = styled.div`
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    justify-content: flex-end;
    gap: ${Spacing._12};
`

const WindowSelector = styled.div`
    display: inline-flex;
    border: ${Border.stroke.medium} solid ${Colors.background.border};
    border-radius: ${Border.radius.medium};
    overflow: hidden;
`

const WindowOption = styled(NoStyleButton)<{ $selected: boolean }>`
    ${Typography.label.small};
    padding: ${Spacing._4} ${Spacing._12};
    color: ${({ $selected }) => ($selected ? Colors.text.ink : Colors.text.muted)};
    background-color: ${({ $selected }) => ($selected ? Colors.background.sub : Colors.background.white)};
    font-variant-numeric: tabular-nums;

    & + & {
        border-left: ${Border.stroke.medium} solid ${Colors.background.border};
    }

    &:focus-visible {
        outline: ${Border.stroke.large} solid ${Colors.control.secondary.highlight};
        outline-offset: -${Border.stroke.large};
    }
`

const UsageBody = styled.div<{ $stale: boolean }>`
    display: flex;
    flex-direction: column;
    gap: ${Spacing._16};
    padding: ${Spacing._16};
    opacity: ${({ $stale }) => ($stale ? 0.6 : 1)};
    transition: opacity 120ms ease;
`

const UsageGroup = styled.div`
    display: flex;
    flex-direction: column;
    gap: ${Spacing._8};
`

const UsageState = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: ${Spacing._16};
    padding: ${Spacing._32};
`

const UsageTable = styled(UserTable)`
    min-width: 720px;
    border-top: ${Border.stroke.medium} solid ${Colors.background.border};
`

const NotFound = styled.div`
    display: flex;
    flex: 1;
    align-items: center;
    justify-content: center;
    background-color: ${Colors.background.page};
`

interface TEmailTestStatus {
    state: 'sending' | 'sent' | 'error'
    message?: string
}

const ALL_EMAIL_TESTS = 'all'

const emailTestStatusLabel = (status: TEmailTestStatus) => {
    if (status.state === 'sending') return 'Sending'
    if (status.state === 'sent') return 'Sent'
    return status.message || 'Failed'
}

const deliveryConfigIssueLabels: Record<Exclude<TEmailDeliveryConfigIssue, ''>, string> = {
    key_missing: 'RESEND_API_KEY is not configured on this server',
    placeholder_key: 'RESEND_API_KEY is a placeholder on this server',
    not_prod: 'this server is not running in prod',
}

// Hides the local part for screen sharing; the domain stays so company accounts are still legible.
const maskEmail = (email: string) => {
    const [local, domain] = email.split('@')
    if (!domain) return '•••'
    return `${local.slice(0, 1)}•••@${domain}`
}

const USAGE_WINDOWS: TInternalUsageWindow[] = ['7d', '30d', '90d']

const goalMetrics = (usage: TInternalUsage): MetricProps[] => [
    {
        label: 'Users with goals',
        value: usage.goals.users,
        detail: `${formatNumber(usage.goals.total)} goals total`,
    },
    {
        label: `Created ${usage.window}`,
        value: usage.goals.created_in_window,
        detail: 'New goals',
    },
    {
        label: 'Active now',
        value: usage.goals.active,
        detail: `${formatNumber(usage.goals.completed)} completed · ${formatNumber(usage.goals.archived)} archived`,
    },
    {
        label: 'With linked tasks',
        value: usage.goals.active_with_linked_tasks,
        detail: `Median ${formatNumber(usage.goals.median_linked_tasks_per_goal)} per active goal`,
    },
]

const routineMetrics = (usage: TInternalUsage): MetricProps[] => [
    {
        label: 'Users with routines',
        value: usage.routines.users,
        detail: `${formatNumber(usage.routines.total)} routines · ${formatNumber(
            usage.routines.created_in_window
        )} new`,
    },
    {
        label: `Days done ${usage.window}`,
        value: usage.routines.days_completed,
        detail: 'Stored completions',
    },
    {
        label: 'Completion rate',
        value: 'Not tracked',
        detail: 'Routine runs are not stored',
    },
    {
        label: 'No completion',
        value: usage.routines.without_completion_in_window,
        detail: `Routines in ${usage.window}`,
    },
]

interface UsagePanelProps {
    maskEmails: boolean
}

const UsagePanel = ({ maskEmails }: UsagePanelProps) => {
    const [usageWindow, setUsageWindow] = useState<TInternalUsageWindow>('30d')
    const { data, isError, isFetching, isPreviousData, refetch } = useGetInternalUsage(usageWindow)
    // Previous figures stay up, dimmed, while another window loads or after a refresh fails.
    const isStale = Boolean(data) && (isPreviousData || isError)

    return (
        <Panel data-testid="usage-panel" aria-busy={isFetching}>
            <PanelHeader>
                <TitleSmall>Goals & Routines</TitleSmall>
                <PanelControls>
                    {isStale && (
                        <LabelSmall color="muted">
                            {isError ? `Refresh failed · as of ${formatDateTime(data?.snapshot_at ?? '')}` : 'Updating'}
                        </LabelSmall>
                    )}
                    <WindowSelector role="group" aria-label="Window">
                        {USAGE_WINDOWS.map((option) => (
                            <WindowOption
                                key={option}
                                type="button"
                                aria-pressed={option === usageWindow}
                                $selected={option === usageWindow}
                                data-testid={`usage-window-${option}`}
                                onClick={() => setUsageWindow(option)}
                            >
                                {option}
                            </WindowOption>
                        ))}
                    </WindowSelector>
                </PanelControls>
            </PanelHeader>
            {!data && !isError && (
                <UsageState>
                    <Spinner />
                </UsageState>
            )}
            {!data && isError && (
                <UsageState>
                    <BodyMedium color="secondary">Could not load usage.</BodyMedium>
                    <GTButton styleType="secondary" value="Retry" onClick={() => refetch()} />
                </UsageState>
            )}
            {data && (
                <>
                    <UsageBody $stale={isStale}>
                        <UsageGroup>
                            <BodySmallUpper color="muted">Goals</BodySmallUpper>
                            <MetricsGrid>
                                {goalMetrics(data).map((metric) => (
                                    <Metric key={metric.label} {...metric} />
                                ))}
                            </MetricsGrid>
                        </UsageGroup>
                        <UsageGroup>
                            <BodySmallUpper color="muted">Routines</BodySmallUpper>
                            <MetricsGrid>
                                {routineMetrics(data).map((metric) => (
                                    <Metric key={metric.label} {...metric} />
                                ))}
                            </MetricsGrid>
                        </UsageGroup>
                    </UsageBody>
                    {data.users.length === 0 ? (
                        <EmptyState>
                            <BodyMedium color="secondary">No goals or routines yet.</BodyMedium>
                        </EmptyState>
                    ) : (
                        <TableScroller>
                            <UsageTable>
                                <thead>
                                    <tr>
                                        <th>User</th>
                                        <th>Status</th>
                                        <th>Goals</th>
                                        <th>Routines</th>
                                        <th>Last activity</th>
                                        <th>Trial</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {data.users.map((user) => (
                                        <tr key={user.email}>
                                            <td>
                                                <UserCell>
                                                    <Email>{maskEmails ? maskEmail(user.email) : user.email}</Email>
                                                    {user.name && !maskEmails && (
                                                        <BodySmall color="secondary">{user.name}</BodySmall>
                                                    )}
                                                </UserCell>
                                            </td>
                                            <td>
                                                <Tag $paid={user.subscription_status === 'active'}>
                                                    {statusLabel(user)}
                                                </Tag>
                                            </td>
                                            <td>
                                                <MonoValue>{formatNumber(user.goal_count)}</MonoValue>
                                            </td>
                                            <td>
                                                <MonoValue>{formatNumber(user.routine_count)}</MonoValue>
                                            </td>
                                            <td>
                                                <MonoValue>{formatDate(user.last_activity_at)}</MonoValue>
                                            </td>
                                            <td>
                                                <MonoValue>{trialLabel(user)}</MonoValue>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </UsageTable>
                        </TableScroller>
                    )}
                </>
            )}
        </Panel>
    )
}

const EmailTestsPanel = () => {
    const { data, isError, isLoading } = useGetInternalEmailTests()
    const { mutate: sendTestEmail, isLoading: isSending } = useSendInternalTestEmail()
    const { mutate: updateEmailDelivery, isLoading: isUpdatingDelivery } = useUpdateEmailDelivery()
    const [statuses, setStatuses] = useState<Record<string, TEmailTestStatus>>({})
    const emailTests = data?.emails
    const delivery = data?.delivery
    // the server cannot reach Resend at all, so a test send would only fail
    const configIssue = delivery?.config_issue ?? ''

    const send = (type: string) => {
        const keys = type === ALL_EMAIL_TESTS ? emailTests?.map((emailTest) => emailTest.key) ?? [] : [type]
        const setEach = (status: TEmailTestStatus) =>
            setStatuses((current) => ({ ...current, ...Object.fromEntries(keys.map((key) => [key, status])) }))
        setEach({ state: 'sending' })
        sendTestEmail(type, {
            onSuccess: (results) =>
                setStatuses((current) => ({
                    ...current,
                    ...Object.fromEntries(
                        results.map((result) => [
                            result.key,
                            result.sent ? { state: 'sent' } : { state: 'error', message: result.error },
                        ])
                    ),
                })),
            onError: () => setEach({ state: 'error', message: 'Request failed' }),
        })
    }

    return (
        <Panel data-testid="email-tests-panel">
            <PanelHeader>
                <TitleSmall>Email</TitleSmall>
                <FeatureFlagControl>
                    <LabelSmall color="muted">Sent to you, marked [TEST]</LabelSmall>
                    <GTButton
                        styleType="secondary"
                        value="Send all"
                        disabled={isSending || !emailTests?.length || !!configIssue}
                        onClick={() => send(ALL_EMAIL_TESTS)}
                    />
                </FeatureFlagControl>
            </PanelHeader>
            {isLoading && <Spinner />}
            {configIssue && (
                <DeliveryOffBanner data-testid="email-delivery-off-banner">
                    <Icon icon={icons.warning} color="gray" />
                    <BodyMedium color="ink">{`Email delivery is off: ${deliveryConfigIssueLabels[configIssue]}`}</BodyMedium>
                </DeliveryOffBanner>
            )}
            {delivery && (
                <FeatureFlagRow data-testid="email-delivery-row">
                    <FeatureFlagDetails>
                        <BodyMedium color="ink">Email to users</BodyMedium>
                        <BodySmall color="secondary">
                            {delivery.switched_on ? 'On' : 'Paused · owed emails send when back on'}
                        </BodySmall>
                        <BodySmall color="muted">
                            {delivery.switch_updated_at
                                ? `Last changed ${formatDateTime(delivery.switch_updated_at)}`
                                : 'Never changed'}
                        </BodySmall>
                    </FeatureFlagDetails>
                    <FeatureFlagControl>
                        <LabelSmall color="muted">{delivery.switched_on ? 'On' : 'Off'}</LabelSmall>
                        <GTSwitch
                            label="Email to users"
                            checked={delivery.switched_on}
                            testId="email-delivery-toggle"
                            disabled={isUpdatingDelivery}
                            onChange={(enabled) => updateEmailDelivery(enabled)}
                        />
                    </FeatureFlagControl>
                </FeatureFlagRow>
            )}
            {(isError || emailTests?.length === 0) && (
                <EmptyState>
                    <BodyMedium color="secondary">{isError ? 'Could not load emails.' : 'No emails.'}</BodyMedium>
                </EmptyState>
            )}
            {emailTests?.map((emailTest) => {
                const status = statuses[emailTest.key]
                return (
                    <FeatureFlagRow key={emailTest.key} data-testid={`${emailTest.key}-email-test`}>
                        <FeatureFlagDetails>
                            <BodyMedium color="ink">{emailTest.name}</BodyMedium>
                            <BodySmall color="secondary">{emailTest.description}</BodySmall>
                        </FeatureFlagDetails>
                        <FeatureFlagControl>
                            {status && (
                                <EmailTestResult $status={status.state}>{emailTestStatusLabel(status)}</EmailTestResult>
                            )}
                            <GTButton
                                styleType="secondary"
                                value="Send test to me"
                                disabled={isSending || !!configIssue}
                                onClick={() => send(emailTest.key)}
                            />
                        </FeatureFlagControl>
                    </FeatureFlagRow>
                )
            })}
        </Panel>
    )
}

interface MetricProps {
    label: string
    value: number | string
    detail: string
    highlight?: boolean
}

const formatNumber = (value: number) => numberFormatter.format(value)

const formatDate = (value: string) => {
    if (!value) return '-'
    return DateTime.fromISO(value).toFormat('MMM d')
}

const formatDateTime = (value: string) => {
    if (!value) return '-'
    return DateTime.fromISO(value).toFormat('MMM d, h:mm a')
}

type TPlanFields = Pick<
    TInternalDashboardUser,
    'subscription_status' | 'is_in_trial' | 'trial_days_remaining' | 'trial_ends_at'
>

const statusLabel = (user: TPlanFields) => {
    if (user.subscription_status === 'active') return 'Paid'
    if (user.subscription_status === 'trialing') return 'Trialing'
    if (user.subscription_status) return user.subscription_status
    if (user.is_in_trial) return 'Trial'
    return 'Expired'
}

const acknowledgmentLabels: Record<TInternalDashboardUser['subscription_acknowledgment'], string> = {
    '': '',
    sent: 'Terms sent',
    pending: 'Terms owed',
}

const userEmailDetail = (user: TInternalDashboardUser) =>
    [user.welcome_email_sent_at ? 'Welcome sent' : '', acknowledgmentLabels[user.subscription_acknowledgment]]
        .filter(Boolean)
        .join(' · ')

const trialLabel = (user: TPlanFields) =>
    user.is_in_trial ? `${formatNumber(user.trial_days_remaining)}d` : formatDate(user.trial_ends_at)

const Metric = ({ label, value, detail, highlight }: MetricProps) => (
    <MetricPanel $highlight={highlight}>
        <BodySmallUpper color="muted">{label}</BodySmallUpper>
        <MetricValue>{typeof value === 'number' ? formatNumber(value) : value}</MetricValue>
        <BodySmall color="secondary">{detail}</BodySmall>
    </MetricPanel>
)

const metrics = (counts: TInternalDashboardCounts): MetricProps[] => [
    {
        label: '30d signups',
        value: counts.signups_last_30_days,
        detail: `${formatNumber(counts.signups_last_7_days)} last 7d`,
    },
    {
        label: 'Activated',
        value: counts.activated_users,
        detail: `${formatNumber(counts.activated_last_7_days)} last 7d`,
    },
    {
        label: 'Paid',
        value: counts.paid_users,
        detail: `${formatNumber(counts.trialing_users)} trialing`,
        highlight: true,
    },
    {
        label: 'Total users',
        value: counts.total_users,
        detail: 'All accounts',
    },
]

const InternalDashboardView = () => {
    const { data, isError, isLoading, isFetching, refetch } = useGetInternalDashboardSummary()
    const {
        data: featureFlags,
        isError: areFeatureFlagsError,
        isLoading: areFeatureFlagsLoading,
    } = useGetInternalFeatureFlags()
    const { mutate: updateFeatureFlags, isLoading: isUpdatingFeatureFlags } = useUpdateInternalFeatureFlags()
    const queryClient = useQueryClient()
    const [maskEmails, setMaskEmails] = useState(false)

    if (isLoading || areFeatureFlagsLoading) return <Spinner />
    if (isError || areFeatureFlagsError || !data || !featureFlags) {
        return (
            <NotFound>
                <TitleSmall>Not found</TitleSmall>
            </NotFound>
        )
    }

    return (
        <DashboardPage data-testid="internal-dashboard">
            <DashboardInner>
                <Header>
                    <HeaderText>
                        <BodySmallUpper color="muted">Internal</BodySmallUpper>
                        <HeadlineMedium color="ink">Relaunch pulse</HeadlineMedium>
                        <BodyMedium color="secondary">{`Updated ${formatDateTime(data.snapshot_at)}`}</BodyMedium>
                    </HeaderText>
                    <HeaderControls>
                        <SwitchLabel>
                            <LabelSmall color="muted">Mask emails</LabelSmall>
                            <GTSwitch
                                label="Mask emails"
                                checked={maskEmails}
                                testId="mask-emails-toggle"
                                onChange={setMaskEmails}
                            />
                        </SwitchLabel>
                        <GTButton
                            styleType="primary"
                            icon={icons.spinner}
                            value={isFetching ? 'Refreshing' : 'Refresh'}
                            disabled={isFetching}
                            onClick={() => {
                                refetch()
                                queryClient.invalidateQueries('internal_usage')
                            }}
                        />
                    </HeaderControls>
                </Header>

                <MetricsGrid>
                    {metrics(data.counts).map((metric) => (
                        <Metric key={metric.label} {...metric} />
                    ))}
                </MetricsGrid>

                <Panel data-testid="feature-flags-panel">
                    <PanelHeader>
                        <TitleSmall>Feature flags</TitleSmall>
                        <LabelSmall color="muted">Internal access stays on</LabelSmall>
                    </PanelHeader>
                    {Object.values(featureFlags).map((flag) => (
                        <FeatureFlagRow key={flag.key}>
                            <FeatureFlagDetails>
                                <BodyMedium color="ink">{flag.name}</BodyMedium>
                                <BodySmall color="secondary">
                                    {flag.enabled ? 'On for everyone' : 'Off for everyone'}
                                </BodySmall>
                                <BodySmall color="muted">
                                    {[
                                        flag.updated_at
                                            ? `Last changed ${formatDateTime(flag.updated_at)}`
                                            : 'Never changed',
                                        `${formatNumber(flag.enabled_user_count)} have it`,
                                        `${formatNumber(flag.opted_in_count)} opted in`,
                                    ].join(' · ')}
                                </BodySmall>
                            </FeatureFlagDetails>
                            <FeatureFlagControl>
                                <LabelSmall color="muted">{flag.enabled ? 'On' : 'Off'}</LabelSmall>
                                <GTSwitch
                                    label={flag.name}
                                    checked={flag.enabled}
                                    testId={`${flag.key}-feature-toggle`}
                                    disabled={isUpdatingFeatureFlags}
                                    onChange={(enabled) => updateFeatureFlags({ [flag.key]: enabled })}
                                />
                            </FeatureFlagControl>
                        </FeatureFlagRow>
                    ))}
                </Panel>

                <UsagePanel maskEmails={maskEmails} />

                <EmailTestsPanel />

                <Panel>
                    <PanelHeader>
                        <TitleSmall>Recent users</TitleSmall>
                        <LabelSmall color="muted">{`${formatNumber(data.recent_users.length)} shown`}</LabelSmall>
                    </PanelHeader>
                    {data.recent_users.length === 0 ? (
                        <EmptyState>
                            <BodyMedium color="secondary">No users yet.</BodyMedium>
                        </EmptyState>
                    ) : (
                        <TableScroller>
                            <UserTable>
                                <thead>
                                    <tr>
                                        <th>User</th>
                                        <th>Joined</th>
                                        <th>Status</th>
                                        <th>Tasks</th>
                                        <th>Completed</th>
                                        <th>Last completion</th>
                                        <th>Trial</th>
                                        <th>Email</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {data.recent_users.map((user) => (
                                        <tr key={user.email}>
                                            <td>
                                                <UserCell>
                                                    <Email>{maskEmails ? maskEmail(user.email) : user.email}</Email>
                                                    {user.name && !maskEmails && (
                                                        <BodySmall color="secondary">{user.name}</BodySmall>
                                                    )}
                                                </UserCell>
                                            </td>
                                            <td>
                                                <MonoValue>{formatDate(user.created_at)}</MonoValue>
                                            </td>
                                            <td>
                                                <Tag $paid={user.subscription_status === 'active'}>
                                                    {statusLabel(user)}
                                                </Tag>
                                            </td>
                                            <td>
                                                <MonoValue>{formatNumber(user.task_count)}</MonoValue>
                                            </td>
                                            <td>
                                                <MonoValue>{formatNumber(user.completed_task_count)}</MonoValue>
                                            </td>
                                            <td>
                                                <MonoValue>{formatDate(user.last_completed_at)}</MonoValue>
                                            </td>
                                            <td>
                                                <MonoValue>{trialLabel(user)}</MonoValue>
                                            </td>
                                            <td>
                                                <UserCell>
                                                    <Tag>{user.email_unsubscribed ? 'Unsubscribed' : 'Subscribed'}</Tag>
                                                    {userEmailDetail(user) && (
                                                        <BodySmall color="secondary">{userEmailDetail(user)}</BodySmall>
                                                    )}
                                                </UserCell>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </UserTable>
                        </TableScroller>
                    )}
                </Panel>
            </DashboardInner>
        </DashboardPage>
    )
}

const InternalDashboardScreen = () => (
    <CalendarContextProvider>
        <DefaultTemplate>
            <InternalDashboardView />
        </DefaultTemplate>
    </CalendarContextProvider>
)

export default InternalDashboardScreen
